import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, Heart, Trash2 } from 'lucide-react';
import { FaWindows, FaApple, FaXbox } from 'react-icons/fa';
import { DiAndroid } from 'react-icons/di';
import { SiOculus, SiPlaystation, SiSteamdeck } from 'react-icons/si';
import { BsNintendoSwitch } from 'react-icons/bs';
import { TbDeviceVisionProFilled } from 'react-icons/tb';
import gsap from 'gsap';
import { setSelectedGameId, setSelectedGame } from '../../redux/slice/game.slice';
import { toggleWishlist, addToCart } from '../../redux/slice/user.slice';
import { getGamePath } from '../../utils/gamePath';
import ProgressiveImage from './ProgressiveImage';

export const PlatformIcon = {
    windows: FaWindows,
    playstation: SiPlaystation,
    xbox: FaXbox,
    nintendo: BsNintendoSwitch,
    quest: SiOculus,
    android: DiAndroid,
    ios: FaApple,
    visionpro: TbDeviceVisionProFilled,
    steamdeck: SiSteamdeck
};



export const PlatformColor = {
    windows: '#0078d7',
    playstation: '#00439c',
    xbox: '#107c10',
    nintendo: '#e60012',
    quest: '#ffffff',
    android: '#3ddc84',
    ios: '#a3aaae',
    visionpro: '#a855f7',
    steamdeck: '#38bdf8'
};

export const PlatformLabel = {
    windows: 'Windows PC',
    playstation: 'PlayStation',
    xbox: 'Xbox',
    nintendo: 'Nintendo Switch',
    quest: 'Meta Quest VR',
    android: 'Android',
    ios: 'iOS',
    visionpro: 'Vision Pro',
    steamdeck: 'Steam Deck'
};

const GameCard = React.memo(({ game, onAddToCart, isWishlistPage = false, className = '', style = {} }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { wishlist } = useSelector((s) => s.userSlice || { wishlist: [] });
    const targetId = game?.id || game?._id;
    const isFav = wishlist?.some(id => (id?._id || id?.id || id) === targetId);
    const cardRef = useRef(null);

    const defaultPlatform = useMemo(() => {
        if (game?.platforms?.windows?.available) return 'windows';
        const firstAvailable = Object.entries(game?.platforms || {}).find(([_, info]) => info?.available);
        return firstAvailable ? firstAvailable[0] : 'windows';
    }, [game?.platforms]);

    const [activePlatform, setActivePlatform] = useState(defaultPlatform);

    useEffect(() => {
        setActivePlatform(defaultPlatform);
    }, [defaultPlatform]);

    const handleCardClick = useCallback((e) => {
        e.preventDefault();
        if (!game) return;
        const currentPath = window.location.pathname;
        const currentScroll = (window.__lenis ? window.__lenis.scroll : null) ?? window.scrollY ?? 0;
        if (currentScroll > 0) {
            sessionStorage.setItem(`cg_scroll_${currentPath}`, String(currentScroll));
        }
        if (targetId) {
            sessionStorage.setItem(`cg_last_game_id_${currentPath}`, String(targetId));
        }
        dispatch(setSelectedGameId(targetId));
        dispatch(setSelectedGame(game));
        navigate(getGamePath(game));
    }, [dispatch, navigate, game, targetId]);

    const handleWishlistClick = useCallback((e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (game) {
            dispatch(toggleWishlist(game));
        }
    }, [dispatch, game]);

    const handleBuyClick = useCallback((e) => {
        e.stopPropagation();
        if (!game) return;
        const selectedPlatform = activePlatform || defaultPlatform;
        if (typeof onAddToCart === 'function') {
            onAddToCart({ ...game, selectedPlatform });
        } else {
            dispatch(addToCart({ ...game, selectedPlatform }));
        }
    }, [dispatch, game, onAddToCart, activePlatform, defaultPlatform]);

    if (!game) return null;

    const currentPlatInfo = game?.platforms?.[activePlatform]
        || (game?.platforms?.[defaultPlatform]?.available ? game?.platforms?.[defaultPlatform] : null)
        || Object.values(game?.platforms || {}).find(p => p?.available);

    const displayOriginal = currentPlatInfo?.originalPrice !== undefined
        ? currentPlatInfo.originalPrice
        : (game?.originalPrice || 0);

    const displayPrice = currentPlatInfo?.price !== undefined
        ? currentPlatInfo.price
        : (game?.price || 0);

    const displayDiscount = currentPlatInfo?.discount !== undefined
        ? currentPlatInfo.discount
        : (displayOriginal > displayPrice && displayOriginal > 0
            ? Math.round(((displayOriginal - displayPrice) / displayOriginal) * 100)
            : (game?.discount || 0));

    return (
        <div
            ref={cardRef}
            id={`game-card-${targetId}`}
            data-game-id={targetId}
            onClick={handleCardClick}
            style={style}
            className={`game-card-item group relative flex flex-col cursor-pointer h-full bg-[#080814]/95 cyber-card-glow transition-all duration-300 overflow-hidden ${className}`}>

            {/* Top Image Box */}
            <div className="relative aspect-[16/10] overflow-hidden cursor-pointer" style={{ borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }}>
                <ProgressiveImage
                    src={game.cover_image?.url || game.image}
                    placeholderSrc={game.cover_image?.lowUrl || game.image}
                    alt={game.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />

                {displayDiscount > 0 && (
                    <div className="absolute top-3 left-3 px-2.5 py-0.5 rounded-md bg-cyber-green text-cyber-black font-cyber font-extrabold text-[9px] tracking-wider shadow-[0_0_12px_rgba(57,255,20,0.5)]">
                        -{displayDiscount}%
                    </div>
                )}

                {/* Rating Badge Overlay at Bottom Left */}
                <div className="absolute bottom-2 left-2.5 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/65 backdrop-blur-md border border-white/15 text-xs font-mono text-white font-bold shadow-md">
                    <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400 drop-shadow-[0_0_6px_rgba(250,204,21,0.6)]" />
                    <span>{Number(game.rating || 4.9).toFixed(1)}</span>
                </div>

                <button
                    type="button"
                    onClick={handleWishlistClick}
                    aria-label={isWishlistPage ? "Remove from wishlist" : (isFav ? "Remove from wishlist" : "Add to wishlist")}
                    title={isWishlistPage ? "Remove from wishlist" : (isFav ? "Remove from wishlist" : "Add to wishlist")}
                    className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-black/65 backdrop-blur-md border border-white/15 flex items-center justify-center text-gray-400 hover:text-cyber-pink hover:border-cyber-pink/60 hover:bg-cyber-pink/20 hover:shadow-[0_0_12px_rgba(255,0,122,0.6)] transition-all duration-300 z-10"
                >
                    {isWishlistPage ? (
                        <Trash2 className="w-3.5 h-3.5 text-cyber-pink drop-shadow-[0_0_6px_rgba(255,0,122,0.6)]" />
                    ) : (
                        <Heart className={`w-3.5 h-3.5 ${isFav ? 'fill-cyber-pink text-cyber-pink drop-shadow-[0_0_8px_rgba(255,0,122,0.8)]' : ''}`} />
                    )}
                </button>
            </div>

            {/* Card Content */}
            <div className="p-3 md:p-4 flex flex-col flex-1 gap-2.5">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-[9px] font-cyber font-bold uppercase tracking-widest text-cyber-purple bg-cyber-purple/15 px-2.5 py-0.5 rounded-md border border-cyber-purple/30 group-hover:border-cyber-purple/60 group-hover:shadow-[0_0_10px_rgba(189,0,255,0.3)] transition-all truncate">
                        {game.category?.name || 'GAME'}
                    </span>
                    {/* Platform Icons in place of rating */}
                    <div className="flex items-center gap-1.5 flex-shrink-0 z-20">
                        {Object.entries(PlatformIcon).map(([plat, Icon]) => {
                            if (!game.platforms?.[plat]?.available) return null;
                            const isActive = (activePlatform || defaultPlatform) === plat;
                            return (
                                <button
                                    key={plat}
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setActivePlatform(plat);
                                    }}
                                    className={`p-0.5 rounded transition-all duration-200 cursor-pointer focus:outline-none flex items-center justify-center ${isActive ? 'scale-110' : 'hover:scale-105'
                                        }`}
                                    title={`${PlatformLabel[plat]} (${isActive ? 'Active' : 'Click to view price'})`}
                                    aria-label={PlatformLabel[plat]}
                                >
                                    <Icon
                                        className="w-3.5 h-3.5 transition-all duration-200"
                                        style={{
                                            color: isActive ? PlatformColor[plat] : '#9ca3af',
                                            filter: isActive
                                                ? `drop-shadow(0 0 6px ${PlatformColor[plat]}80)`
                                                : 'grayscale(100%)',
                                            opacity: isActive ? 1 : 0.4
                                        }}
                                    />
                                </button>
                            );
                        })}
                    </div>
                </div>

                <h3
                    className="font-cyber font-bold text-sm text-white transition-colors duration-300 line-clamp-1 uppercase tracking-tight cursor-pointer"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const currentPath = window.location.pathname;
                        const currentScroll = (window.__lenis ? window.__lenis.scroll : null) ?? window.scrollY ?? 0;
                        if (currentScroll > 0) {
                            sessionStorage.setItem(`cg_scroll_${currentPath}`, String(currentScroll));
                        }
                        if (targetId) {
                            sessionStorage.setItem(`cg_last_game_id_${currentPath}`, String(targetId));
                        }
                        dispatch(setSelectedGameId(game.id || game._id));
                        dispatch(setSelectedGame(game));
                        navigate(getGamePath(game));
                    }}
                >
                    {game.title}
                </h3>

                {(game?.description || game?.tagline) && (
                    <p className="text-gray-400 text-[11px] line-clamp-1 leading-relaxed h-4 font-sans">
                        {game.description || game.tagline}
                    </p>
                )}

                {/* Price & Action Section */}
                <div className="flex items-center justify-between pt-3.5 mt-auto">
                    <div className="flex flex-col">
                        {displayDiscount > 0 && (
                            <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500 line-through font-mono tracking-tighter">
                                    ${Number(displayOriginal).toFixed(2)}
                                </span>
                                <span className="text-cyber-green text-[9px] font-cyber font-bold">-{displayDiscount}%</span>
                            </div>
                        )}
                        <span className="font-cyber font-black text-base sm:text-lg text-white transition-colors">
                            {displayPrice === 0 ? 'FREE' : `$${Number(displayPrice).toFixed(2)}`}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={handleBuyClick}
                        className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-cyber font-bold uppercase tracking-wider text-cyber-cyan border border-cyber-cyan/40 transition-all duration-200 active:scale-95 group/btn"
                    >
                        <ShoppingCart className="w-3.5 h-3.5 group-hover/btn:scale-110 transition-transform text-cyber-cyan" /> BUY
                    </button>
                </div>
            </div>
        </div>
    );
});

export const GameCardSkeleton = React.memo(({ className = '', style = {} }) => (
    <div
        style={style}
        className={`game-card-item relative flex flex-col h-full bg-[#080814]/85 backdrop-blur-xl border border-white/15 overflow-hidden animate-pulse ${className}`}
    >
        {/* Top Image Box */}
        <div className="relative aspect-[16/10] overflow-hidden bg-white/[0.04]" style={{ borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }}>
            {/* Rating Badge */}
            <div className="absolute bottom-2 left-2.5 w-12 h-6 rounded-lg bg-black/60 border border-white/15" />
            {/* Wishlist Button Placeholder */}
            <div className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-black/60 border border-white/15" />
        </div>

        {/* Card Content */}
        <div className="p-4 sm:p-5 flex flex-col flex-1 gap-2.5">
            <div className="flex items-center justify-between gap-2">
                <div className="h-4 w-16 sm:w-20 rounded-md bg-cyber-purple/20 border border-cyber-purple/30" />
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <div className="w-3.5 h-3.5 rounded-full bg-white/10" />
                    <div className="w-3.5 h-3.5 rounded-full bg-white/10" />
                    <div className="w-3.5 h-3.5 rounded-full bg-white/10" />
                </div>
            </div>

            <div className="h-4 sm:h-4.5 w-3/4 bg-white/10 rounded mt-0.5" />
            <div className="h-3.5 w-full bg-white/5 rounded" />

            {/* Price & Action Section */}
            <div className="flex items-center justify-between pt-3.5 mt-auto">
                <div className="h-5 w-16 bg-white/10 rounded" />
                <div className="h-8 w-20 rounded-lg bg-cyber-cyan/20 border border-cyber-cyan/30" />
            </div>
        </div>
    </div>
));

export const GameSkeleton = GameCardSkeleton;

export default GameCard;
